import { Box, Typography } from "@mui/material";

import { FieldDisagreementLine } from "./wasteStreamDiscrepancyIndicator";

/**
 * Cap on the rows shown in the cell tooltip. A stream aggregates many manifest
 * lines, so a field can have more disagreements than read comfortably in a
 * hover — beyond this we collapse the rest into an "and N more…" line and let
 * the user open the stream to see them all.
 */
const MAX_TOOLTIP_ROWS = 3;

interface WasteStreamDiscrepancyTooltipProps {
  /** Field name shown as the tooltip header, e.g. "Form Code". */
  fieldLabel: string;
  /** Per-source disagreeing values, in canonical source order. */
  lines: FieldDisagreementLine[];
  /** Field-issue descriptions (e.g. state-waste-code rule findings), listed after the values. */
  issueLabels?: string[];
  /** Shown when there are no value/issue rows (e.g. a discrepancy row with no stored values). */
  fallback?: string;
  maxRows?: number;
}

/**
 * Body for the grid's per-cell (!) tooltip: the field name, then the actual
 * disagreeing per-source values (and any field-issue findings), capped with an
 * "and N more…" overflow line. Mirrors the manifest drawer's
 * `SourcesDisagreeTooltip` style — the field name replaces its fixed "Sources
 * disagree on this value" header.
 */
export function WasteStreamDiscrepancyTooltip({
  fieldLabel,
  lines,
  issueLabels = [],
  fallback,
  maxRows = MAX_TOOLTIP_ROWS,
}: WasteStreamDiscrepancyTooltipProps) {
  const valueRows = lines.map((line) => `${line.label}: ${line.value}`);
  const rows = [...valueRows, ...issueLabels];
  const shown = rows.slice(0, maxRows);
  const remaining = rows.length - shown.length;

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        {fieldLabel}
      </Typography>
      {/* Show the count when there are no per-source value rows — even alongside
          issue labels — so a value-less comparison discrepancy still surfaces. */}
      {valueRows.length === 0 && fallback && (
        <Typography variant="caption" component="div">
          {fallback}
        </Typography>
      )}
      {shown.map((row, index) => (
        <Typography key={index} variant="caption" component="div">
          {row}
        </Typography>
      ))}
      {remaining > 0 && (
        <Typography
          variant="caption"
          component="div"
          sx={{ fontStyle: "italic", color: "text.secondary", mt: 0.25 }}
        >
          and {remaining} more…
        </Typography>
      )}
    </Box>
  );
}
